import { useEffect, useMemo, useState } from "react";
import { router } from "@inertiajs/react";
import Header from "./Component/Header";

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const formatCoin = (value) =>
    new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const isPercentValue = (value) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    return (
        normalized === "percent" ||
        normalized === "percentage" ||
        normalized === "%" ||
        normalized === "1" ||
        normalized === "true"
    );
};

const formatRupiahWithSpace = (value) =>
    formatCurrency(value).replace(/^Rp\s?/, "Rp ");

const isCustomVariant = (variant) => Number(variant?.prod_var_type ?? 0) === 1;

const formatBonus = (value, bonusType) =>
    Number(bonusType) === 0
        ? `${new Intl.NumberFormat("id-ID", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
          }).format(Number(value) || 0)}%`
        : formatCurrency(value);

export default function Checkout({
    products = [],
    selectedProductId: initialSelectedProductId = null,
    selectedVariantId: initialSelectedVariantId = null,
    initialAgentCode = null,
}) {
    const productOptions = Array.isArray(products) ? products : [];

    const [selectedProductId, setSelectedProductId] = useState(() => {
        if (initialSelectedProductId !== null && initialSelectedProductId !== undefined) {
            return String(initialSelectedProductId);
        }

        return "";
    });

    const selectedProduct = useMemo(() => {
        return (
            productOptions.find(
                (product) => String(product.prod_id) === String(selectedProductId)
            ) || null
        );
    }, [productOptions, selectedProductId]);

    const [selectedVariantId, setSelectedVariantId] = useState(() => {
        if (initialSelectedVariantId !== null && initialSelectedVariantId !== undefined) {
            return String(initialSelectedVariantId);
        }

        const initialProduct =
            productOptions.find(
                (product) => String(product.prod_id) === String(initialSelectedProductId)
            ) || null;

        const firstVariant = initialProduct?.variants?.[0];
        return firstVariant ? String(firstVariant.prod_var_id) : "";
    });

    useEffect(() => {
        const navigationEntries = window.performance.getEntriesByType("navigation");
        const isReload = navigationEntries[0]?.type === "reload";

        if (!isReload) return;

        const params = new URLSearchParams();
        if (initialSelectedProductId !== null && initialSelectedProductId !== undefined && String(initialSelectedProductId) !== "") {
            params.set("product_id", String(initialSelectedProductId));
        }
        if (initialSelectedVariantId !== null && initialSelectedVariantId !== undefined && String(initialSelectedVariantId) !== "") {
            params.set("variant_id", String(initialSelectedVariantId));
        }
        if (initialAgentCode !== null && initialAgentCode !== undefined && String(initialAgentCode).trim() !== "") {
            params.set("agent_code", String(initialAgentCode).trim().toUpperCase());
        }

        const nextUrl = params.toString()
            ? `/product/checkout?${params.toString()}`
            : "/product/checkout";

        router.replace(nextUrl, {
            preserveState: true,
            preserveScroll: true,
        });
    }, []);

    useEffect(() => {
        if (!selectedProduct) {
            setSelectedVariantId((prev) => (prev === "" ? prev : ""));
            return;
        }

        const variants = Array.isArray(selectedProduct.variants)
            ? selectedProduct.variants
            : [];

        const variantExists = variants.some(
            (variant) => String(variant.prod_var_id) === String(selectedVariantId)
        );

        if (variantExists) return;

        const nextVariantId = variants[0] ? String(variants[0].prod_var_id) : "";
        setSelectedVariantId((prev) => (prev === nextVariantId ? prev : nextVariantId));
    }, [selectedProduct, selectedVariantId]);

    const [availablePayments, setAvailablePayments] = useState([]);
    const [availablePaymentError, setAvailablePaymentError] = useState("");
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentLoaded, setPaymentLoaded] = useState(false);
    const [submittingOrder, setSubmittingOrder] = useState(false);
    const [momoRecipientId, setMomoRecipientId] = useState("");
    const [whatsAppNumber, setWhatsAppNumber] = useState("");
    const [agentCode, setAgentCode] = useState(() =>
        initialAgentCode !== null && initialAgentCode !== undefined
            ? String(initialAgentCode).trim().toUpperCase()
            : ""
    );
    const [customNominal, setCustomNominal] = useState("");
    const [momoRecipientLoading, setMomoRecipientLoading] = useState(false);
    const [momoRecipientError, setMomoRecipientError] = useState("");
    const [momoRecipientData, setMomoRecipientData] = useState(null);
    const paymentOptions = useMemo(() => {
        const toArray = (value) => (Array.isArray(value) ? value : []);
        let methods = [];

        if (Array.isArray(availablePayments)) {
            methods = availablePayments;
        } else if (Array.isArray(availablePayments?.data)) {
            methods = availablePayments.data;
        } else if (Array.isArray(availablePayments?.result)) {
            methods = availablePayments.result;
        } else if (Array.isArray(availablePayments?.payment_methods)) {
            methods = availablePayments.payment_methods;
        } else if (Array.isArray(availablePayments?.paymentMethods)) {
            methods = availablePayments.paymentMethods;
        } else if (Array.isArray(availablePayments?.channels)) {
            methods = availablePayments.channels;
        } else {
            methods = toArray(
                Object.values(availablePayments || {}).find((value) =>
                    Array.isArray(value)
                )
            );
        }

        return [...methods].sort((a, b) => {
            const labelA = String(
                a?.nama ?? a?.name ?? a?.channel_name ?? a?.method_name ?? ""
            ).toLowerCase();
            const labelB = String(
                b?.nama ?? b?.name ?? b?.channel_name ?? b?.method_name ?? ""
            ).toLowerCase();

            return labelA.localeCompare(labelB);
        });
    }, [availablePayments]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
    const selectedVariant = useMemo(() => {
        if (!selectedProduct || !selectedVariantId) return null;

        const variants = Array.isArray(selectedProduct.variants)
            ? selectedProduct.variants
            : [];

        return (
            variants.find(
                (variant) => String(variant.prod_var_id) === String(selectedVariantId)
            ) || null
        );
    }, [selectedProduct, selectedVariantId]);
    const selectedPaymentOption = useMemo(() => {
        if (!selectedPaymentMethod) return null;

        return (
            paymentOptions.find((method, index) => {
                const code = String(
                    method?.kode ??
                        method?.pay_code ??
                        method?.channel_code ??
                        method?.method ??
                        method?.name ??
                        `method-${index}`
                );

                return code === selectedPaymentMethod;
            }) || null
        );
    }, [paymentOptions, selectedPaymentMethod]);
    const selectedPaymentMethodLabel = useMemo(() => {
        if (!selectedPaymentOption) return "-";

        return (
            selectedPaymentOption?.nama ??
            selectedPaymentOption?.channel_name ??
            selectedPaymentOption?.method_name ??
            selectedPaymentOption?.name ??
            selectedPaymentMethod
        );
    }, [selectedPaymentOption, selectedPaymentMethod]);
    const shouldShowPaymentFeeLabel = (method) =>
        Number(method?.pml_fee_type ?? 1) === 2;
    const getPaymentFeeBadgeLabel = (method) => {
        const feeValue = toNumber(method?.biaya);

        if (isPercentValue(method?.percent)) {
            return `Adm Fee ${feeValue}%`;
        }

        return `Adm Fee ${formatCurrency(feeValue)}`;
    };
    useEffect(() => {
        setAgentCode(
            initialAgentCode !== null && initialAgentCode !== undefined
                ? String(initialAgentCode).trim().toUpperCase()
                : ""
        );
    }, [initialAgentCode]);

    useEffect(() => {
        if (!selectedVariant || !isCustomVariant(selectedVariant)) {
            setCustomNominal((prev) => (prev === "" ? prev : ""));
        }
    }, [selectedVariant]);

    const customNominalAmount = useMemo(() => toNumber(customNominal), [customNominal]);
    const priceAmount = useMemo(() => {
        if (!selectedVariant) return 0;

        return isCustomVariant(selectedVariant)
            ? customNominalAmount
            : toNumber(selectedVariant?.prod_var_price);
    }, [selectedVariant, customNominalAmount]);
    const totalEarnedCoin = useMemo(() => {
        if (!selectedVariant) return 0;

        return isCustomVariant(selectedVariant)
            ? customNominalAmount
            : toNumber(selectedVariant?.total_earned_coin);
    }, [selectedVariant, customNominalAmount]);
    const bonusCoin = useMemo(() => {
        if (!selectedVariant) return 0;

        if (isCustomVariant(selectedVariant)) {
            if (customNominalAmount < 300000) return 0;
            if (customNominalAmount < 1000000) return totalEarnedCoin * 0.01;

            return totalEarnedCoin * 0.02;
        }

        const bonusValue = toNumber(selectedVariant.prod_var_bonus);
        const bonusType = Number(selectedVariant.prod_var_bonus_type ?? 0);

        if (bonusType === 0) {
            return (totalEarnedCoin * bonusValue) / 100;
        }

        return bonusValue;
    }, [selectedVariant, totalEarnedCoin, customNominalAmount]);
    const displayedPaymentFee = useMemo(() => {
        if (!selectedPaymentOption) return 0;
        if (Number(selectedPaymentOption?.pml_fee_type ?? 1) !== 2) return 0;

        const feeValue = toNumber(selectedPaymentOption?.biaya);

        if (isPercentValue(selectedPaymentOption?.percent)) {
            return (priceAmount * feeValue) / 100;
        }

        return feeValue;
    }, [selectedPaymentOption, priceAmount]);
    const totalPayment = useMemo(
        () => priceAmount + displayedPaymentFee,
        [priceAmount, displayedPaymentFee]
    );
    const filteredPaymentOptions = useMemo(() => {
        if (totalPayment <= 0) return paymentOptions;

        return paymentOptions.filter((method) => {
            const minimal = toNumber(method?.minimal);
            const maksimal = toNumber(method?.maksimal);

            if (minimal > 0 && totalPayment < minimal) {
                return false;
            }

            if (maksimal > 0 && totalPayment > maksimal) {
                return false;
            }

            return true;
        });
    }, [paymentOptions, totalPayment]);
    const isCustomNominalValid = useMemo(() => {
        if (!selectedVariant || !isCustomVariant(selectedVariant)) return true;

        return customNominalAmount > 0;
    }, [selectedVariant, customNominalAmount]);
    const isRecipientValid = Boolean(
        String(momoRecipientId || "").trim() !== "" &&
            !momoRecipientLoading &&
            !momoRecipientError &&
            momoRecipientData
    );
    const canSelectProduct = isRecipientValid;
    const canSelectVariant = canSelectProduct && Boolean(selectedProduct);
    const canSelectPayment =
        canSelectVariant && Boolean(selectedVariant) && isCustomNominalValid;
    const canConfirm = canSelectPayment && Boolean(selectedPaymentMethod);
    const isConfirmationDisabled = !canConfirm;

    useEffect(() => {
        if (!selectedPaymentMethod) return;

        const isSelectedMethodAvailable = filteredPaymentOptions.some(
            (method, index) => {
                const code = String(
                    method?.kode ??
                        method?.pay_code ??
                        method?.channel_code ??
                        method?.method ??
                        method?.name ??
                        `method-${index}`
                );

                return code === selectedPaymentMethod;
            }
        );

        if (!isSelectedMethodAvailable) {
            setSelectedPaymentMethod("");
        }
    }, [filteredPaymentOptions, selectedPaymentMethod]);

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const pollCheckoutUrl = async (invoiceCode) => {
        for (let attempt = 1; attempt <= 3; attempt += 1) {
            const response = await window.axios.get("/checkout/fetch-checkout-url", {
                params: { invoice_code: invoiceCode },
            });

            const trxId = response?.data?.data?.trx_id;
            const checkoutUrl = response?.data?.data?.checkout_url;

            if (checkoutUrl) {
                return checkoutUrl;
            }

            if ((!trxId || trxId === "-") && attempt < 3) {
                await sleep(5000);
                continue;
            }

            break;
        }

        return null;
    };

    const handleBuyConfirmation = async () => {
        const payload = {
            prod_var_id: selectedVariant?.prod_var_id ?? null,
            payment_code: selectedPaymentMethod || null,
            momo_recipient_code: momoRecipientId || null,
            agent_code: agentCode || null,
            momo_recipient_phone: whatsAppNumber || null,
            custom_nominal: isCustomVariant(selectedVariant) ? priceAmount : null,
        };

        console.log(payload);
        let paymentTab = null;

        try {
            if (submittingOrder) return;
            setSubmittingOrder(true);

            paymentTab = window.open("about:blank", "_blank");
            if (paymentTab) {
                paymentTab.document.write("<p style='font-family:sans-serif'>Menyiapkan halaman pembayaran. Mohon tunggu...</p>");
            }

            const response = await window.axios.post("/checkout/create-invoice", {
                ...payload,
                payment_method: payload.payment_code,
            });

            const invoiceCode = response?.data?.data?.invoice?.inv_code;
            const salesOrderCode = response?.data?.data?.sales_order?.so_code;
            const salesOrderCreatorId =
                response?.data?.data?.sales_order?.rec_creator ?? null;
            if (!invoiceCode) {
                if (paymentTab) paymentTab.close();
                throw new Error("Kode invoice tidak ditemukan dari respons pembuatan invoice.");
            }

            const checkoutUrl = await pollCheckoutUrl(invoiceCode);

            if (checkoutUrl) {
                if (paymentTab) {
                    paymentTab.location.href = checkoutUrl;
                } else {
                    window.open(checkoutUrl, "_blank");
                }

                if (salesOrderCode) {
                    const salesOrderParams = new URLSearchParams({
                        soCode: String(salesOrderCode),
                    });

                    if (salesOrderCreatorId !== null && salesOrderCreatorId !== undefined) {
                        salesOrderParams.set(
                            "rec_creator",
                            String(salesOrderCreatorId)
                        );
                    }

                    window.location.assign(
                        `/sales-order?${salesOrderParams.toString()}`
                    );
                }
            } else {
                if (paymentTab) paymentTab.close();
                window.alert("URL checkout belum siap. Silakan coba lagi.");
            }

        } catch (error) {
            if (paymentTab) paymentTab.close();
            console.error("Failed to create invoice:", error);
            window.alert(
                error?.response?.data?.message ??
                    error?.message ??
                    "Gagal memproses checkout."
            );
        } finally {
            setSubmittingOrder(false);
        }
    };

    useEffect(() => {
        const recipientCode = String(momoRecipientId || "").trim();

        if (recipientCode === "") {
            setMomoRecipientLoading(false);
            setMomoRecipientError("");
            setMomoRecipientData(null);
            return;
        }

        if (!/^\d+$/.test(recipientCode)) {
            setMomoRecipientLoading(false);
            setMomoRecipientError("Kode penerima harus berupa angka.");
            setMomoRecipientData(null);
            return;
        }

        let cancelled = false;
        setMomoRecipientLoading(true);
        setMomoRecipientError("");
        setMomoRecipientData(null);

        const timeoutId = window.setTimeout(async () => {
            try {
                const response = await window.axios.post("/checkout/momo-query-user", {
                    momo_recipient_code: Number(recipientCode),
                });
                if (cancelled) return;

                const payload = response?.data ?? {};
                if (Number(payload?.code) === 0) {
                    setMomoRecipientData(payload?.data ?? null);
                } else {
                    setMomoRecipientData(null);
                    setMomoRecipientError(payload?.message ?? "Gagal memeriksa data penerima.");
                }
            } catch (error) {
                if (cancelled) return;
                setMomoRecipientData(null);
                setMomoRecipientError(
                    error?.response?.data?.message ??
                        "Gagal memeriksa data penerima."
                );
            } finally {
                if (!cancelled) {
                    setMomoRecipientLoading(false);
                }
            }
        }, 3000);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [momoRecipientId]);

    useEffect(() => {
        if (!canSelectPayment || paymentLoaded) {
            return;
        }

        const controller = new AbortController();

        const fetchAvailablePayments = async () => {
            setPaymentLoading(true);
            setAvailablePaymentError("");

            try {
                const response = await window.axios.get("/checkout/payment-methods", {
                    timeout: 15000,
                    signal: controller.signal,
                });
                console.log(response?.data?.data)
                setAvailablePayments(response?.data?.data ?? []);
                setPaymentLoaded(true);
            } catch (error) {
                if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
                    return;
                }

                const errorMessage =
                    (error?.code === "ECONNABORTED"
                        ? "Permintaan melebihi batas waktu saat memuat metode pembayaran."
                        : null) ??
                    error?.response?.data?.error ??
                    error?.response?.data?.message ??
                    "Gagal memuat metode pembayaran.";
                setAvailablePaymentError(errorMessage);
                setAvailablePayments([]);
            } finally {
                setPaymentLoading(false);
            }
        };

        fetchAvailablePayments();

        return () => {
            controller.abort();
        };
    }, [canSelectPayment, paymentLoaded]);

    useEffect(() => {
        if (paymentOptions.length === 0) {
            setSelectedPaymentMethod((prev) => (prev === "" ? prev : ""));
            return;
        }

        setSelectedPaymentMethod((prev) => {
            if (!prev) return "";

            const exists = paymentOptions.some((method, index) => {
                const code = String(
                    method?.kode ??
                        method?.pay_code ??
                        method?.channel_code ??
                        method?.method ??
                        method?.name ??
                        `method-${index}`
                );

                return code === prev;
            });

            return exists ? prev : "";
        });
    }, [paymentOptions]);

    useEffect(() => {
        if (canSelectPayment) return;

        setSelectedPaymentMethod((prev) => (prev === "" ? prev : ""));
    }, [canSelectPayment]);

    return (
        <>
            <Header />

            <main id="content">
                <div className="w-full max-w-4xl mx-auto">
                    <div className="bg-card border border-card-line rounded-lg shadow-md">
                        <div className="border-b border-card-footer px-5 py-4">
                            <h1 className="text-lg font-semibold text-foreground">Checkout Pesanan</h1>
                            <p className="mt-1 text-sm text-muted-foreground-1">
                                Selesaikan langkah berikut secara berurutan: penerima, produk, varian,
                                metode pembayaran, lalu konfirmasi.
                            </p>
                        </div>

                        <div className="p-5 space-y-6">
                            <section className="rounded-xl border border-line-2 bg-surface-1/40 p-4 space-y-3">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                                        1
                                    </span>
                                    <div>
                                        <h2 className="text-base font-semibold text-foreground">
                                            ID Penerima
                                        </h2>
                                        <p className="text-sm text-muted-foreground-1">
                                            Validasi penerima sebelum memilih produk.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label
                                        htmlFor="momo-recipient-id"
                                        className="block text-xs font-medium text-muted-foreground-1"
                                    >
                                        ID Penerima
                                    </label>
                                    <input
                                        id="momo-recipient-id"
                                        type="text"
                                        value={momoRecipientId}
                                        onChange={(event) =>
                                            setMomoRecipientId(
                                                event.target.value.replace(/\D/g, "")
                                            )
                                        }
                                        placeholder="Masukkan ID penerima"
                                        className="w-full rounded-lg border border-line-2 bg-layer px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-fuchsia-400/60"
                                    />
                                    {momoRecipientLoading && (
                                        <p className="text-xs text-muted-foreground-1">
                                            Sedang memeriksa penerima...
                                        </p>
                                    )}
                                    {momoRecipientError && (
                                        <p className="text-xs text-red-500">
                                            {momoRecipientError}
                                        </p>
                                    )}
                                    {momoRecipientData && !momoRecipientError && (
                                        <div className="flex items-center gap-2 rounded-lg border border-line-2 bg-layer p-3">
                                            {momoRecipientData?.avatar ? (
                                                <img
                                                    src={momoRecipientData.avatar}
                                                    alt="Avatar penerima Momo"
                                                    className="h-10 w-10 rounded-full object-cover"
                                                />
                                            ) : null}
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium text-foreground">
                                                    {momoRecipientData?.nick_name ?? "-"}
                                                </p>
                                                <p className="truncate text-xs text-muted-foreground-1">
                                                    ID: {momoRecipientData?.user_id ?? momoRecipientId}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <label
                                            htmlFor="whatsapp-number"
                                            className="block text-xs font-medium text-muted-foreground-1"
                                        >
                                            Nomor WhatsApp
                                        </label>
                                        <input
                                            id="whatsapp-number"
                                            type="text"
                                            value={whatsAppNumber}
                                            onChange={(event) =>
                                                setWhatsAppNumber(
                                                    event.target.value.replace(/[^\d+]/g, "")
                                                )
                                            }
                                            placeholder="Masukkan nomor WhatsApp"
                                            className="w-full rounded-lg border border-line-2 bg-layer px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-fuchsia-400/60"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            htmlFor="agent-code"
                                            className="block text-xs font-medium text-muted-foreground-1"
                                        >
                                            Kode Agen
                                        </label>
                                        <input
                                            id="agent-code"
                                            type="text"
                                            value={agentCode}
                                            onChange={(event) =>
                                                setAgentCode(event.target.value.toUpperCase())
                                            }
                                            placeholder="Masukkan kode agen"
                                            className="w-full rounded-lg border border-line-2 bg-layer px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-fuchsia-400/60"
                                        />
                                        {initialAgentCode ? (
                                            <p className="text-xs text-muted-foreground-1">
                                                Kode referral diterapkan dari link yang dibagikan.
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </section>

                            <section
                                className={`rounded-xl border p-4 space-y-3 ${
                                    canSelectProduct
                                        ? "border-line-2 bg-surface-1/40"
                                        : "border-line-2 bg-surface-1/20 opacity-60"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                                        2
                                    </span>
                                    <div>
                                        <h2 className="text-base font-semibold text-foreground">
                                            Pilih Produk
                                        </h2>
                                        <p className="text-sm text-muted-foreground-1">
                                            Pilih produk setelah penerima valid.
                                        </p>
                                    </div>
                                </div>

                                {!canSelectProduct ? (
                                    <p className="text-sm text-muted-foreground-1">
                                        Masukkan ID penerima yang valid untuk membuka pilihan produk.
                                    </p>
                                ) : productOptions.length === 0 ? (
                                    <div className="rounded-lg border border-line-2 bg-layer p-4 text-sm text-muted-foreground-1">
                                        Data produk belum tersedia.
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {productOptions.map((product) => (
                                            <label
                                                key={product.prod_id}
                                                className={`block cursor-pointer rounded-lg border px-3 py-2 transition ${
                                                    String(selectedProductId) ===
                                                    String(product.prod_id)
                                                        ? "border-fuchsia-400 bg-fuchsia-500/20 shadow-[0_0_10px_rgba(217,70,239,0.35)]"
                                                        : "border-line-2 hover:bg-layer"
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="product_id"
                                                    value={String(product.prod_id)}
                                                    checked={
                                                        String(selectedProductId) ===
                                                        String(product.prod_id)
                                                    }
                                                    onChange={(event) =>
                                                        setSelectedProductId(event.target.value)
                                                    }
                                                    className="sr-only"
                                                />
                                                <span className="text-sm text-foreground">
                                                    {product.prod_name}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </section>

                            <section
                                className={`rounded-xl border p-4 space-y-3 ${
                                    canSelectVariant
                                        ? "border-line-2 bg-surface-1/40"
                                        : "border-line-2 bg-surface-1/20 opacity-60"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                                        3
                                    </span>
                                    <div>
                                        <h2 className="text-base font-semibold text-foreground">
                                            Pilih Varian
                                        </h2>
                                        <p className="text-sm text-muted-foreground-1">
                                            Pilih satu varian untuk produk yang dipilih.
                                        </p>
                                    </div>
                                </div>

                                {!canSelectProduct ? (
                                    <p className="text-sm text-muted-foreground-1">
                                        Selesaikan langkah 1 terlebih dahulu.
                                    </p>
                                ) : !selectedProduct ? (
                                    <p className="text-sm text-muted-foreground-1">
                                        Pilih produk untuk melihat variannya.
                                    </p>
                                ) : !Array.isArray(selectedProduct.variants) ||
                                  selectedProduct.variants.length === 0 ? (
                                    <p className="text-sm text-muted-foreground-1">
                                        Produk ini tidak memiliki varian aktif.
                                    </p>
                                ) : (
                                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                        {selectedProduct.variants.map((variant) => (
                                            <label
                                                key={variant.prod_var_id}
                                                className={`block cursor-pointer rounded-xl border p-4 transition ${
                                                    String(selectedVariantId) ===
                                                    String(variant.prod_var_id)
                                                        ? "border-fuchsia-400 bg-fuchsia-500/20 shadow-[0_0_10px_rgba(217,70,239,0.35)]"
                                                        : "border-line-2 bg-layer/40 hover:bg-layer"
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="variant_id"
                                                    value={String(variant.prod_var_id)}
                                                    checked={
                                                        String(selectedVariantId) ===
                                                        String(variant.prod_var_id)
                                                    }
                                                    onChange={(event) =>
                                                        setSelectedVariantId(event.target.value)
                                                    }
                                                    className="sr-only"
                                                />
                                                <span className="flex items-start justify-between gap-4">
                                                    <span className="space-y-3">
                                                        <span className="block">
                                                            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                                                <span>{variant.prod_var_name}</span>
                                                                {Number(variant.prod_var_type) === 1 && (
                                                                    <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                                                        Kustom
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="mt-1 block text-lg font-semibold text-foreground">
                                                                {isCustomVariant(variant)
                                                                    ? "Nominal kustom"
                                                                    : formatCurrency(variant.prod_var_price)}
                                                            </span>
                                                        </span>

                                                        <span className="flex flex-wrap gap-2 text-xs text-muted-foreground-1">
                                                            <span className="rounded-full border border-line-2 px-2.5 py-1">
                                                                {isCustomVariant(variant)
                                                                    ? "Coin mengikuti nominal"
                                                                    : `${formatCoin(variant.total_earned_coin)} Coin`}
                                                            </span>
                                                            <span className="rounded-full border border-line-2 px-2.5 py-1">
                                                                {isCustomVariant(variant)
                                                                    ? "Bonus 0% / 1% / 2%"
                                                                    : `Bonus ${formatBonus(variant.prod_var_bonus, variant.prod_var_bonus_type)}`}
                                                            </span>
                                                        </span>
                                                    </span>

                                                    <span
                                                        className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                                                            String(selectedVariantId) ===
                                                            String(variant.prod_var_id)
                                                                ? "border-fuchsia-300 bg-fuchsia-400"
                                                                : "border-line-2 bg-transparent"
                                                        }`}
                                                        aria-hidden="true"
                                                    >
                                                        {String(selectedVariantId) ===
                                                        String(variant.prod_var_id) ? (
                                                            <span className="h-2.5 w-2.5 rounded-full bg-white" />
                                                        ) : null}
                                                    </span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                {selectedVariant && isCustomVariant(selectedVariant) && (
                                    <div className="rounded-lg border border-line-2 bg-layer p-4 space-y-2">
                                        <label
                                            htmlFor="custom-nominal"
                                            className="block text-sm font-medium text-foreground"
                                        >
                                            Nominal Kustom
                                        </label>
                                        <input
                                            id="custom-nominal"
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={customNominal}
                                            onChange={(event) =>
                                                setCustomNominal(
                                                    event.target.value.replace(/\D/g, "")
                                                )
                                            }
                                            placeholder="Masukkan nominal"
                                            className="w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-fuchsia-400/60"
                                        />
                                        <p className="text-xs text-muted-foreground-1">
                                            Total coin mengikuti nominal. Aturan bonus: di bawah
                                            Rp 300.000 tidak mendapat bonus, Rp 300.000 sampai di bawah Rp
                                            1.000.000 mendapat 1%, dan Rp 1.000.000 atau lebih mendapat 2%.
                                        </p>
                                        {!isCustomNominalValid && (
                                            <p className="text-xs text-red-500">
                                                Masukkan nominal lebih dari 0 untuk melanjutkan.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </section>

                            <section
                                className={`rounded-xl border p-4 space-y-3 ${
                                    canSelectPayment
                                        ? "border-line-2 bg-surface-1/40"
                                        : "border-line-2 bg-surface-1/20 opacity-60"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                                        4
                                    </span>
                                    <div>
                                        <h2 className="text-base font-semibold text-foreground">
                                            Pilih Metode Pembayaran
                                        </h2>
                                        <p className="text-sm text-muted-foreground-1">
                                            Opsi pembayaran akan muncul setelah varian dipilih.
                                        </p>
                                    </div>
                                </div>

                                {!canSelectPayment ? (
                                    <p className="text-sm text-muted-foreground-1">
                                        {selectedVariant && isCustomVariant(selectedVariant) && !isCustomNominalValid
                                            ? "Masukkan nominal kustom terlebih dahulu."
                                            : "Selesaikan langkah 1 sampai 3 terlebih dahulu."}
                                    </p>
                                ) : (
                                    <>
                                            <div className="rounded-lg border border-line-2 bg-layer p-4 space-y-2">
                                            <p className="text-sm text-foreground">
                                                Produk: {selectedProduct?.prod_name ?? "-"}
                                            </p>
                                            <p className="text-sm text-foreground">
                                                Varian: {selectedVariant?.prod_var_name ?? "-"}
                                            </p>
                                            <p className="text-sm text-foreground">
                                                Total Coin Diperoleh: {formatCoin(totalEarnedCoin)}
                                            </p>
                                            <p className="text-sm text-foreground">
                                                Bonus Coin: {formatCoin(bonusCoin)}
                                            </p>
                                            <p className="text-sm text-foreground">
                                                Harga: {formatCurrency(priceAmount)}
                                            </p>
                                            <p className="text-sm text-foreground">
                                                Biaya: {formatCurrency(displayedPaymentFee)}
                                            </p>
                                            <p className="text-sm text-foreground">
                                                Total: {formatCurrency(totalPayment)}
                                            </p>
                                        </div>

                                        {availablePaymentError && (
                                            <div className="space-y-2">
                                                <p className="text-xs text-red-500">
                                                    {availablePaymentError}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setPaymentLoaded(false);
                                                        setAvailablePaymentError("");
                                                    }}
                                                    className="text-xs font-medium text-fuchsia-300 hover:text-fuchsia-200"
                                                >
                                                    Coba Lagi
                                                </button>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            {paymentLoading ? (
                                                <div className="rounded-lg border border-line-2 bg-layer px-3 py-2 text-sm text-muted-foreground-1">
                                                    Sedang memuat metode pembayaran...
                                                </div>
                                            ) : filteredPaymentOptions.length === 0 ? (
                                                <div className="rounded-lg border border-line-2 bg-layer px-3 py-2 text-sm text-muted-foreground-1">
                                                    Tidak ada metode pembayaran yang cocok dengan total saat ini.
                                                </div>
                                            ) : (
                                                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                                    {filteredPaymentOptions.map((method, index) => {
                                                        const code = String(
                                                            method?.kode ??
                                                                method?.pay_code ??
                                                                method?.channel_code ??
                                                                method?.method ??
                                                                method?.name ??
                                                                `method-${index}`
                                                        );
                                                        const name =
                                                            method?.nama ??
                                                            method?.channel_name ??
                                                            method?.method_name ??
                                                            code;
                                                        const description =
                                                            method?.description ??
                                                            method?.group ??
                                                            method?.type ??
                                                            "";

                                                        return (
                                                            <label
                                                                key={code}
                                                                className={`block cursor-pointer rounded-xl border p-4 transition ${
                                                                    selectedPaymentMethod === code
                                                                        ? "border-fuchsia-400 bg-fuchsia-500/20 shadow-[0_0_10px_rgba(217,70,239,0.35)]"
                                                                        : "border-line-2 bg-layer/40 hover:bg-layer"
                                                                }`}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name="payment_method"
                                                                    value={code}
                                                                    checked={selectedPaymentMethod === code}
                                                                    onChange={(event) =>
                                                                        setSelectedPaymentMethod(
                                                                            event.target.value
                                                                        )
                                                                    }
                                                                    className="sr-only"
                                                                />
                                                                <span className="flex items-start justify-between gap-4">
                                                                    <span className="space-y-3">
                                                                        <span className="block">
                                                                        <span className="block text-sm font-semibold text-foreground">
                                                                            {name}
                                                                        </span>
                                                                        {shouldShowPaymentFeeLabel(method) ? (
                                                                            <span className="mt-1 inline-flex rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                                                                {getPaymentFeeBadgeLabel(method)}
                                                                            </span>
                                                                        ) : null}
                                                                        {description ? (
                                                                            <span className="mt-1 block text-xs text-muted-foreground-1">
                                                                                {description}
                                                                                </span>
                                                                            ) : null}
                                                                        </span>

                                                                        {method?.logo ? (
                                                                            <span className="flex h-12 items-center">
                                                                                <img
                                                                                    src={method.logo}
                                                                                    alt={`${name} logo`}
                                                                                    className="h-10 w-auto object-contain"
                                                                                />
                                                                            </span>
                                                                        ) : null}
                                                                    </span>

                                                                    <span
                                                                        className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                                                                            selectedPaymentMethod === code
                                                                                ? "border-fuchsia-300 bg-fuchsia-400"
                                                                                : "border-line-2 bg-transparent"
                                                                        }`}
                                                                        aria-hidden="true"
                                                                    >
                                                                        {selectedPaymentMethod === code ? (
                                                                            <span className="h-2.5 w-2.5 rounded-full bg-white" />
                                                                        ) : null}
                                                                    </span>
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {selectedPaymentOption && (
                                            <div className="space-y-2 text-sm text-muted-foreground-1 leading-relaxed">
                                                {selectedPaymentOption?.logo ? (
                                                    <div className="flex justify-center py-3">
                                                        <img
                                                            src={selectedPaymentOption.logo}
                                                            alt="Payment method logo"
                                                            className="h-14 w-auto object-contain"
                                                        />
                                                    </div>
                                                ) : (
                                                    <p>
                                                        {selectedPaymentOption?.description ??
                                                            selectedPaymentOption?.group ??
                                                            selectedPaymentOption?.type ??
                                                            ""}
                                                    </p>
                                                )}
                                                {shouldShowPaymentFeeLabel(selectedPaymentOption) ? (
                                                    <div>
                                                        <span className="inline-flex rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                                                            {getPaymentFeeBadgeLabel(selectedPaymentOption)}
                                                        </span>
                                                    </div>
                                                ) : null}
                                                {selectedPaymentOption?.guide?.title && (
                                                    <p className="text-base font-semibold text-foreground">
                                                        {selectedPaymentOption.guide.title}
                                                    </p>
                                                )}
                                                {!selectedPaymentOption?.guide?.title &&
                                                    selectedPaymentOption?.guide_title && (
                                                        <p className="text-base font-semibold text-foreground">
                                                            {selectedPaymentOption.guide_title}
                                                        </p>
                                                    )}
                                                {selectedPaymentOption?.guide?.payment_guide && (
                                                    <p className="whitespace-pre-line text-sm leading-7">
                                                        {selectedPaymentOption.guide.payment_guide}
                                                    </p>
                                                )}
                                                {!selectedPaymentOption?.guide?.payment_guide &&
                                                    selectedPaymentOption?.payment_guide && (
                                                        <p className="whitespace-pre-line text-sm leading-7">
                                                            {selectedPaymentOption.payment_guide}
                                                        </p>
                                                    )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>

                            <section
                                className={`rounded-xl border p-4 space-y-3 ${
                                    canConfirm
                                        ? "border-line-2 bg-surface-1/40"
                                        : "border-line-2 bg-surface-1/20 opacity-60"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                                        5
                                    </span>
                                    <div>
                                        <h2 className="text-base font-semibold text-foreground">
                                            Konfirmasi
                                        </h2>
                                        <p className="text-sm text-muted-foreground-1">
                                            Tinjau detail checkout sebelum melanjutkan ke pembayaran.
                                        </p>
                                    </div>
                                </div>

                                {!canConfirm ? (
                                    <p className="text-sm text-muted-foreground-1">
                                        Selesaikan langkah 1 sampai 4 terlebih dahulu.
                                    </p>
                                ) : (
                                    <div className="space-y-4 text-sm">
                                        <div className="grid gap-3 lg:grid-cols-[1.3fr_0.9fr]">
                                            <div className="rounded-xl border border-line-2 bg-surface-1/50 p-4 space-y-3">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <p className="text-xs uppercase tracking-wide text-muted-foreground-1">
                                                            Produk
                                                        </p>
                                                        <p className="text-base font-semibold text-foreground">
                                                            {selectedProduct?.prod_name ?? "-"}
                                                        </p>
                                                        <p className="mt-1 text-sm text-muted-foreground-1">
                                                            {selectedVariant?.prod_var_name ?? "-"}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs uppercase tracking-wide text-muted-foreground-1">
                                                            Pembayaran
                                                        </p>
                                                        <p className="text-sm font-medium text-foreground">
                                                            {selectedPaymentMethodLabel}
                                                        </p>
                                                        {shouldShowPaymentFeeLabel(selectedPaymentOption) ? (
                                                            <p className="mt-1 text-xs text-amber-700">
                                                                {getPaymentFeeBadgeLabel(selectedPaymentOption)}
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <div className="grid gap-2 sm:grid-cols-2">
                                                    <div className="rounded-lg border border-line-2 bg-layer/50 px-3 py-2">
                                                        <p className="text-xs text-muted-foreground-1">
                                                            ID Penerima
                                                        </p>
                                                        <p className="font-medium text-foreground">
                                                            {momoRecipientId || "-"}
                                                        </p>
                                                    </div>
                                                    <div className="rounded-lg border border-line-2 bg-layer/50 px-3 py-2">
                                                        <p className="text-xs text-muted-foreground-1">
                                                            WhatsApp
                                                        </p>
                                                        <p className="font-medium text-foreground">
                                                            {whatsAppNumber || "-"}
                                                        </p>
                                                    </div>
                                                    <div className="rounded-lg border border-line-2 bg-layer/50 px-3 py-2 sm:col-span-2">
                                                        <p className="text-xs text-muted-foreground-1">
                                                            Kode Agen
                                                        </p>
                                                        <p className="font-medium text-foreground">
                                                            {agentCode || "-"}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="rounded-lg border border-line-2 bg-layer/50 px-3 py-2">
                                                    <p className="text-xs text-muted-foreground-1">
                                                        Profil Penerima
                                                    </p>
                                                    {momoRecipientData ? (
                                                        <div className="mt-1 flex items-center gap-2">
                                                            {momoRecipientData?.avatar ? (
                                                                <img
                                                                    src={momoRecipientData.avatar}
                                                                    alt="Recipient avatar"
                                                                    className="h-8 w-8 rounded-full object-cover"
                                                                />
                                                            ) : null}
                                                            <div className="min-w-0">
                                                                <p className="truncate font-medium text-foreground">
                                                                    {momoRecipientData?.nick_name ?? "-"}
                                                                </p>
                                                                <p className="truncate text-xs text-muted-foreground-1">
                                                                    ID: {momoRecipientData?.user_id ?? momoRecipientId ?? "-"}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="font-medium text-foreground">
                                                            {momoRecipientId ? `ID: ${momoRecipientId}` : "-"}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="rounded-xl border border-line-2 bg-surface-1/50 p-4 space-y-3">
                                                <div className="grid gap-2">
                                                    <div className="flex items-center justify-between rounded-lg border border-line-2 bg-layer/50 px-3 py-2">
                                                        <span className="text-xs text-muted-foreground-1">
                                                            Coin Diperoleh
                                                        </span>
                                                        <span className="font-medium text-foreground">
                                                            {formatCoin(totalEarnedCoin)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between rounded-lg border border-line-2 bg-layer/50 px-3 py-2">
                                                        <span className="text-xs text-muted-foreground-1">
                                                            Bonus Coin
                                                        </span>
                                                        <span className="font-medium text-foreground">
                                                            {formatCoin(bonusCoin)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between rounded-lg border border-line-2 bg-layer/50 px-3 py-2">
                                                        <span className="text-xs text-muted-foreground-1">
                                                            Harga
                                                        </span>
                                                        <span className="font-medium text-foreground">
                                                            {formatRupiahWithSpace(priceAmount)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between rounded-lg border border-line-2 bg-layer/50 px-3 py-2">
                                                        <span className="text-xs text-muted-foreground-1">
                                                            Biaya
                                                        </span>
                                                        <span className="font-medium text-foreground">
                                                            {formatRupiahWithSpace(displayedPaymentFee)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="rounded-lg border border-fuchsia-400/50 bg-fuchsia-500/10 px-3 py-3">
                                                    <p className="text-xs uppercase tracking-wide text-muted-foreground-1">
                                                        Total Pembayaran
                                                    </p>
                                                    <p className="mt-1 text-xl font-semibold text-foreground">
                                                        {formatRupiahWithSpace(totalPayment)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-end border-t border-line-2 pt-3">
                                            <button
                                                type="button"
                                                onClick={handleBuyConfirmation}
                                                disabled={submittingOrder || isConfirmationDisabled}
                                                className="inline-flex items-center rounded-lg border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50 disabled:pointer-events-none"
                                            >
                                                {submittingOrder ? "Sedang diproses..." : "Konfirmasi"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </section>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
